import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Modal, Input, Select, Descriptions } from "antd";
import { useNavigate } from "react-router-dom";
import Icon from "@mui/material/Icon";
import MDBadge from "components/MDBadge";
import MDButton from "components/MDButton";
import DashboardLayout from "pages/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/Navbars/DashboardNavbar";
import MDTypography from "components/MDTypography";
import DataTable from "pages/Tables/DataTable";
import { useToast } from "components/Toast";

import { landlordList, landlordPass, landlordReject } from "api";

const { Option } = Select;
const { TextArea } = Input;

const LandlordApproval = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const [reviewData, setReviewData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [searchText, setSearchText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchParams, setSearchParams] = useState({
    keyword: "",
    status: "",
  });

  // 详情弹窗
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  // 图片预览弹窗
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");

  const handleRowClick = (index) => {
    setCurrentItem(reviewData[index]);
    setDetailVisible(true);
  };

  const handleCloseDetail = () => {
    setDetailVisible(false);
    setCurrentItem(null);
  };

  const handleOpenPreview = (imgUrl) => {
    setPreviewImage(imgUrl);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
  };

  const fetchApprovalData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await landlordList({
        page: currentPage + 1,
        page_size: pageSize,
        keyword: searchParams.keyword,
        status: searchParams.status,
      });

      if (response.status === 0) {
        setReviewData(response.result.list || []);
        setTotal(response.result.total || 0);
      } else {
        toast(`加载失败：${response.msg}`, "error");
        setReviewData([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("获取列表失败", error);
      toast("网络异常，加载失败", "error");
    } finally {
      setLoading(false);
    }
  }, [searchParams, currentPage, pageSize, toast]);

  useEffect(() => {
    fetchApprovalData();
  }, [fetchApprovalData]);

  const handleSearch = () => {
    setSearchParams({
      keyword: searchText,
      status: selectedStatus === "all" ? "" : selectedStatus,
    });
    setCurrentPage(0);
  };

  const resetFilters = () => {
    setSearchText("");
    setSelectedStatus("all");
    setSearchParams({ keyword: "", status: "" });
    setCurrentPage(0);
  };

  const handlePageChange = (newPageIndex) => {
    setCurrentPage(newPageIndex);
  };

  const handleApprove = useCallback(
    (item) => {
      Modal.confirm({
        title: "确认审核通过",
        content: `是否通过【${item.host_name}】的入驻申请？`,
        okText: "确认",
        cancelText: "取消",
        onOk: async () => {
          try {
            setLoading(true);
            const res = await landlordPass({ id: item.id });
            if (res.status !== 0) throw new Error(res.msg);
            toast("审核通过成功", "success");
            fetchApprovalData();
          } catch (err) {
            toast(err.message, "error");
          } finally {
            setLoading(false);
          }
        },
      });
    },
    [fetchApprovalData, toast]
  );

  const handleReject = useCallback(
    (item) => {
      let reason = "";
      Modal.confirm({
        title: "确认驳回申请",
        content: (
          <div>
            <p>驳回【${item.host_name}】的入驻申请？</p>
            <TextArea
              rows={3}
              placeholder="请输入驳回原因"
              onChange={(e) => (reason = e.target.value.trim())}
            />
          </div>
        ),
        okText: "确认驳回",
        cancelText: "取消",
        onOk: async () => {
          if (!reason) {
            toast("请输入驳回原因", "warning");
            return;
          }
          try {
            setLoading(true);
            const res = await landlordReject({
              id: item.id,
              reject_reason: reason,
            });
            if (res.status !== 0) throw new Error(res.msg);
            toast("已驳回申请", "success");
            fetchApprovalData();
          } catch (err) {
            toast(err.message, "error");
          } finally {
            setLoading(false);
          }
        },
      });
    },
    [fetchApprovalData, toast]
  );

  const StatusBadge = ({ status }) => {
    switch (status) {
      case 1:
        return <MDBadge badgeContent="审核中" color="warning" size="sm" />;
      case 2:
        return <MDBadge badgeContent="已通过" color="success" size="sm" />;
      case 3:
        return <MDBadge badgeContent="已驳回" color="error" size="sm" />;
      default:
        return <MDBadge badgeContent="未知" color="dark" size="sm" />;
    }
  };

  const { columns, rows } = useMemo(
    () => ({
      columns: [
        { Header: "申请人", accessor: "host_name", width: "180px", align: "left" },
        { Header: "联系人", accessor: "contact_person", width: "160px", align: "left" },
        { Header: "房源类型", accessor: "room_type", width: "140px", align: "left" },
        { Header: "营业执照", accessor: "business_license", width: "220px", align: "left" },
        { Header: "状态", accessor: "status", width: "120px", align: "center" },
        { Header: "操作", accessor: "action", width: "160px", align: "right" },
      ],

      rows: reviewData.map((item) => ({
        host_name: item.host_name,
        contact_person: item.contact_person,
        room_type: item.room_type,
        business_license: item.business_license || "-",
        status: <StatusBadge status={item.status} />,
        action: (
          <MDTypography
            component="div"
            variant="caption"
            sx={{
              display: "flex",
              gap: "4px",
              justifyContent: "flex-end",
              whiteSpace: "nowrap",
            }}
          >
            {item.status === 1 && (
              <>
                <MDButton
                  color="success"
                  size="small"
                  sx={{ px: 1, minWidth: "auto" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleApprove(item);
                  }}
                  disabled={loading}
                >
                  通过
                </MDButton>
                <MDButton
                  color="error"
                  size="small"
                  sx={{ px: 1, minWidth: "auto" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReject(item);
                  }}
                  disabled={loading}
                >
                  驳回
                </MDButton>
              </>
            )}
          </MDTypography>
        ),
      })),
    }),
    [reviewData, loading, handleApprove, handleReject]
  );

  const pendingCount = reviewData.filter((item) => item.status === 1).length;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px 24px",
        borderBottom: "1px solid #e0e0e0",
        marginBottom: "16px",
      }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", marginTop: "8px" }}>
            <Icon sx={{
              fontWeight: "bold",
              color: ({ palette: { warning } }) => warning.main,
              marginRight: "8px",
            }}>pending</Icon>
            <MDTypography variant="body2" color="textSecondary">
              <strong>{pendingCount} 条待审核</strong> | 共 {total} 条申请
            </MDTypography>
          </div>
        </div>
      </div>

      <div style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        padding: "16px 24px",
        marginBottom: "24px",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        flexWrap: "wrap",
        gap: "16px",
      }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <MDTypography variant="body2" style={{ marginRight: 12 }}>状态：</MDTypography>
          <Select value={selectedStatus} onChange={setSelectedStatus} style={{ width: 160 }} size="small">
            <Option value="all">全部</Option>
            <Option value={1}>审核中</Option>
            <Option value={2}>已通过</Option>
            <Option value={3}>已驳回</Option>
          </Select>
        </div>

        <div style={{ display: "flex", alignItems: "center", flexGrow: 1, maxWidth: 400 }}>
          <MDTypography variant="body2" style={{ marginRight: 12, whiteSpace: "nowrap" }}>搜索：</MDTypography>
          <Input
            placeholder="搜索申请人 / 手机号"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            size="small"
            style={{ flexGrow: 1 }}
          />
        </div>

        <div style={{ display: "flex", gap: "8px", whiteSpace: "nowrap" }}>
          <MDButton color="info" size="small" onClick={handleSearch} disabled={loading} sx={{ minWidth: "auto", px: 2 }}>搜索</MDButton>
          <MDButton color="info" variant="outlined" size="small" onClick={resetFilters} sx={{ minWidth: "auto", px: 2 }}>重置</MDButton>
        </div>
      </div>

      <div>
        <DataTable
          table={{ columns, rows }}
          showTotalEntries
          isSorted={false}
          noEndBorder
          entriesPerPage={{ defaultValue: 10, entries: [10, 20, 50] }}
          loading={loading}
          total={total}
          currentPage={currentPage}
          changePage={handlePageChange}
          canSearch={false}
          onRowClick={handleRowClick}
        />
      </div>

      {/* 详情弹窗 */}
      <Modal
        title="入驻申请详情"
        visible={detailVisible}
        onCancel={handleCloseDetail}
        footer={null}
        width={750}
        maskClosable
        zIndex={9999}
      >
        {currentItem && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="申请人">{currentItem.host_name}</Descriptions.Item>
            <Descriptions.Item label="申请人手机号">{currentItem.host_phone}</Descriptions.Item>
            <Descriptions.Item label="联系人">{currentItem.contact_person}</Descriptions.Item>
            <Descriptions.Item label="联系人电话">{currentItem.contact_phone || "-"}</Descriptions.Item>
            <Descriptions.Item label="房源状态">{currentItem.house_status || "-"}</Descriptions.Item>
            <Descriptions.Item label="房源类型">{currentItem.room_type}</Descriptions.Item>
            <Descriptions.Item label="总面积">{currentItem.total_area || "-"}平方米</Descriptions.Item>
            <Descriptions.Item label="申请时间">{currentItem.created_at}</Descriptions.Item>
            <Descriptions.Item label="审核状态">
              <StatusBadge status={currentItem.status} />
            </Descriptions.Item>

            <Descriptions.Item label="营业执照">
              {currentItem.business_license ? (
                <img
                  src={currentItem.business_license}
                  alt="营业执照"
                  style={{
                    maxWidth: "400px",
                    maxHeight: "200px",
                    objectFit: "contain",
                    cursor: "pointer",
                    borderRadius: 4
                  }}
                  onClick={() => handleOpenPreview(currentItem.business_license)}
                />
              ) : "-"}
            </Descriptions.Item>

            {currentItem.reject_reason && (
              <Descriptions.Item label="最后一次驳回原因" style={{ color: "red" }}>
                {currentItem.reject_reason}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* 🔥 修复关闭 + 最高层级 */}
      <Modal
        visible={previewOpen}
        onCancel={handleClosePreview}
        footer={null}
        width="90%"
        centered
        maskClosable
        zIndex={99999}
      >
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}>
          <img
            src={previewImage}
            alt="预览"
            style={{
              maxWidth: "100%",
              maxHeight: "85vh",
              objectFit: "contain",
            }}
          />
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default LandlordApproval;